/**
 * StepDetailsSidebar.tsx
 *
 * Slide-in panel that shows the full input/output payload for any DAG node
 * the user clicks (non-approval nodes).  Approval nodes continue to use the
 * ReviewSidebar, so the two panels are mutually exclusive.
 *
 * Data strategy (cheapest first):
 *   1. If the observability store has already hydrated step records for this
 *      run (e.g. the user visited the Audit Log tab), read directly from the
 *      store — zero network request.
 *   2. Otherwise fetch GET /v1/runs/{runId}/steps?limit=200 and find the step
 *      by ID.  The list endpoint is the only step-level endpoint the backend
 *      currently exposes.
 *
 * Layout (mirrors ReviewSidebar.tsx):
 *   ┌──────────────────────────────┐
 *   │ Header: dept badge · label   │
 *   ├──────────────────────────────┤
 *   │ Status · timing strip        │
 *   ├──────────────────────────────┤
 *   │ Input payload  (JSON viewer) │
 *   ├──────────────────────────────┤
 *   │ Output payload (JSON viewer) │
 *   └──────────────────────────────┘
 */

import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { X } from 'lucide-react'

import { api, apiErrorMessage }    from '../api/client'
import { classifyStepError }       from '../lib/classifyStepError'
import { useObservabilityStore }   from '../store/useObservabilityStore'
import { useMediaQuery }           from '../hooks/useMediaQuery'
import type { GoDAGNode }          from './DAGCanvas'
import type { StepRecord }         from '../store/useObservabilityStore'

// ── Department accent (kept in sync with DAGCanvas / ReviewSidebar) ───────────

const DEPT_ACCENT: Record<string, string> = {
  Research:    '#38bdf8',
  HR:          '#60a5fa',
  Finance:     '#34d399',
  Recruitment: '#a78bfa',
  Legal:       '#f472b6',
  Sales:       '#fb923c',
  Operations:  '#94a3b8',
  Engineering: '#f87171',
  Product:     '#fbbf24',
}
const DEPT_DEFAULT = '#64748b'

// ── Status colour map ─────────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  completed:            '#22c55e',
  succeeded:            '#22c55e',
  running:              '#3b82f6',
  waiting_for_approval: '#f59e0b',
  failed:               '#ef4444',
  pending:              '#475569',
}

// ── Inline JSON viewer (same lightweight renderer as ReviewSidebar) ───────────

function escapeStr(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')
}

function JsonValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  const pad = { paddingLeft: 14 } as const
  if (value === null)             return <span className="text-[#64748b]">null</span>
  if (value === undefined)        return <span className="text-[#64748b]">undefined</span>
  if (typeof value === 'boolean') return <span className="text-[#c084fc]">{String(value)}</span>
  if (typeof value === 'number')  return <span className="text-[#fb923c]">{value}</span>
  if (typeof value === 'string')  return <span className="text-[#4ade80]">"{escapeStr(value)}"</span>

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[#334155]">[]</span>
    return (
      <>
        <span className="text-[#334155]">{'['}</span>
        <div style={pad}>
          {value.map((item, i) => (
            <div key={i}>
              <JsonValue value={item} depth={depth + 1} />
              {i < value.length - 1 && <span className="text-[#334155]">,</span>}
            </div>
          ))}
        </div>
        <span className="text-[#334155]">{']'}</span>
      </>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) return <span className="text-[#334155]">{'{}'}</span>
    return (
      <>
        <span className="text-[#334155]">{'{'}</span>
        <div style={pad}>
          {entries.map(([k, v], i) => (
            <div key={k}>
              <span className="text-[#60a5fa]">"{k}"</span>
              <span className="text-[#334155]">: </span>
              <JsonValue value={v} depth={depth + 1} />
              {i < entries.length - 1 && <span className="text-[#334155]">,</span>}
            </div>
          ))}
        </div>
        <span className="text-[#334155]">{'}'}</span>
      </>
    )
  }

  return <>{String(value)}</>
}

// ── Payload block ─────────────────────────────────────────────────────────────

function PayloadBlock({
  label,
  payload,
}: {
  label: string
  payload: Record<string, unknown> | undefined | null
}) {
  const isEmpty = !payload || Object.keys(payload).length === 0
  return (
    <div className="flex flex-col gap-[8px]">
      <span className="text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480]">
        {label}
      </span>
      {isEmpty ? (
        <div className="text-[12px] text-[#3a5068] italic leading-[1.5] px-4 py-3 bg-[rgba(6,10,16,0.5)] border border-[rgba(148,163,184,0.08)] rounded-[10px]">
          No payload
        </div>
      ) : (
        <div
          className="sds-body-scroll font-mono text-[11.5px] leading-[1.65] text-[#8fa3bd] bg-[rgba(6,10,16,0.7)] border border-[rgba(148,163,184,0.1)] rounded-[10px] px-4 py-[14px] overflow-x-auto break-words max-h-[260px] overflow-y-auto"
          role="region"
          aria-label={`${label} payload`}
        >
          <JsonValue value={payload} />
        </div>
      )}
    </div>
  )
}

// ── Duration helper ───────────────────────────────────────────────────────────

function fmtDuration(startedAt?: string, finishedAt?: string): string | null {
  if (!startedAt || !finishedAt) return null
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime()
  if (isNaN(ms) || ms < 0) return null
  if (ms < 1000)  return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

function fmtTime(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    })
  } catch {
    return iso
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export type StepDetailsSidebarProps = {
  /** The live GoDAGNode for the selected step.  null = closed. */
  node:    GoDAGNode | null
  runId:   string
  onClose: () => void
}

type FetchState =
  | { kind: 'idle'    }
  | { kind: 'loading' }
  | { kind: 'error';  message: string }
  | { kind: 'ready';  step: StepRecord }

function canLeaveFeedback(status?: string): boolean {
  return status === 'succeeded' || status === 'failed'
}

export function StepDetailsSidebar({ node, runId, onClose }: StepDetailsSidebarProps) {
  const isMobile = useMediaQuery('(max-width: 767px)')

  // ── Read from Zustand store first (zero-cost when already hydrated) ──────────
  const storeStep = useObservabilityStore(
    (s) => (node ? s.steps[node.id] : undefined)
  )

  // ── Local fetch state (used only when store has no data) ─────────────────────
  const [fetchState, setFetchState] = useState<FetchState>({ kind: 'idle' })
  const [selectedFeedback, setSelectedFeedback] = useState<number | null>(null)
  const [feedbackNote, setFeedbackNote] = useState('')
  const [feedbackSaving, setFeedbackSaving] = useState(false)
  const [feedbackError, setFeedbackError] = useState<string | null>(null)

  useEffect(() => {
    // Nothing to load when closed.
    if (!node) {
      setFetchState({ kind: 'idle' })
      return
    }

    // Store already has fresh data — skip the network.
    if (storeStep) {
      setFetchState({ kind: 'ready', step: storeStep })
      return
    }

    // Fetch from the list endpoint and find our step by ID.
    let cancelled = false
    setFetchState({ kind: 'loading' })

    api
      .get<{ items: StepRecord[] | null }>(
        `/v1/runs/${encodeURIComponent(runId)}/steps`,
        { params: { limit: 200 } },
      )
      .then((res) => {
        if (cancelled) return
        const step = (res.items ?? []).find((s) => s.id === node.id)
        if (step) {
          setFetchState({ kind: 'ready', step })
        } else {
          // Step not in the list yet (very fast click on a just-admitted run).
          setFetchState({ kind: 'error', message: 'Step record not found in the run yet — try again shortly.' })
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setFetchState({
          kind: 'error',
          message: apiErrorMessage(err, 'Failed to load step details'),
        })
      })

    return () => { cancelled = true }
  // Re-fetch whenever the selected node changes or the runId changes.
  // storeStep intentionally excluded — if it arrives after mount the effect
  // above already checked it; we don't want to re-run the whole effect on every
  // SSE-driven store update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id, runId])

  // Sync store data into fetchState when it arrives mid-open
  // (e.g. user opens the panel then the observability tab hydrates).
  useEffect(() => {
    if (storeStep && node && fetchState.kind !== 'ready') {
      setFetchState({ kind: 'ready', step: storeStep })
    }
  }, [storeStep, node, fetchState.kind])

  useEffect(() => {
    if (fetchState.kind !== 'ready') {
      setSelectedFeedback(null)
      setFeedbackNote('')
      setFeedbackError(null)
      setFeedbackSaving(false)
      return
    }

    setSelectedFeedback(typeof fetchState.step.feedback === 'number' ? fetchState.step.feedback : null)
    setFeedbackNote(fetchState.step.feedbackNote ?? '')
    setFeedbackError(null)
    setFeedbackSaving(false)
  }, [fetchState])

  // ── Keyboard: Escape closes ──────────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape' && node) onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [node, onClose])

  // ── Derived display values ───────────────────────────────────────────────────
  const isOpen     = node !== null
  const deptColor  = DEPT_ACCENT[node?.department ?? ''] ?? DEPT_DEFAULT
  const statusColor = STATUS_COLOR[node?.status ?? ''] ?? DEPT_DEFAULT

  const step = fetchState.kind === 'ready' ? fetchState.step : null
  const duration = fmtDuration(step?.startedAt, step?.finishedAt)

  async function persistFeedback(feedback: number, note: string) {
    if (!node || !step) return
    setFeedbackSaving(true)
    setFeedbackError(null)

    try {
      await api.post(`/v1/runs/${encodeURIComponent(runId)}/steps/${encodeURIComponent(step.id)}/feedback`, {
        feedback,
        note,
      })

      setFetchState((current) => {
        if (current.kind !== 'ready') return current
        return {
          kind: 'ready',
          step: {
            ...current.step,
            feedback,
            feedbackNote: note,
          },
        }
      })
    } catch (err) {
      setFeedbackError(apiErrorMessage(err, 'Failed to save feedback'))
      throw err
    } finally {
      setFeedbackSaving(false)
    }
  }

  async function handleFeedbackChoice(feedback: number) {
    setSelectedFeedback(feedback)
    try {
      await persistFeedback(feedback, feedbackNote)
    } catch {
      // Error state is handled inside persistFeedback; keep the selected state
      // visible so the user can retry or add a note.
    }
  }

  async function handleFeedbackSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedFeedback) return
    try {
      await persistFeedback(selectedFeedback, feedbackNote)
    } catch {
      // Error state is already populated for the user.
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop — pointer-events:none keeps canvas panning/clicking intact */}
      <div
        className={`absolute inset-0 bg-[rgba(4,8,14,0.32)] pointer-events-none transition-opacity duration-[260ms] z-[9] ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className={[
          'absolute top-0 right-0 w-[360px] max-w-full h-full z-[11]',
          'flex flex-col overflow-hidden',
          'bg-[rgba(10,16,24,0.97)] border-l border-[rgba(148,163,184,0.13)] backdrop-blur-[12px]',
          'transition-transform duration-[260ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]',
          isOpen ? 'translate-x-0' : 'translate-x-full',
          isMobile ? 'fixed bottom-0 left-0 right-0 max-h-[80vh] rounded-t-2xl' : '',
        ].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={node ? `Step details: ${node.label}` : 'Step details panel'}
      >
        {node && (
          <>
            {/* ── Header ── */}
            <div className="relative px-5 pt-[18px] pb-4 border-b border-[rgba(148,163,184,0.13)] flex-shrink-0">
              <div className="flex items-center gap-2 mb-[10px]">
                {/* Department / step-type badge */}
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] font-[800] tracking-[0.1em] uppercase border border-transparent whitespace-nowrap"
                  style={{
                    color:       deptColor,
                    background:  `${deptColor}16`,
                    borderColor: `${deptColor}30`,
                  }}
                >
                  {node.department || node.stepType}
                </span>

                {/* Step index */}
                <span className="text-[10px] font-semibold text-[#4e6480] tabular-nums">
                  step #{node.stepIndex}
                </span>
              </div>

              {/* Label */}
              <h2 className="text-[16px] font-[800] text-[#dce9f7] m-0 mb-[6px] leading-[1.3] pr-7 tracking-[-0.02em]">
                {node.label}
              </h2>

              {/* Skill ID */}
              {node.skillId && (
                <p className="font-mono text-[10.5px] text-[#4a6278] m-0 truncate" title={node.skillId}>
                  {node.skillId}
                </p>
              )}

              {/* Close button */}
              <button
                className="absolute top-[14px] right-[14px] w-6 h-6 flex items-center justify-center border-none bg-transparent text-[#4e6480] cursor-pointer rounded-[6px] text-[12px] transition-colors duration-[140ms] leading-none hover:bg-[rgba(148,163,184,0.1)] hover:text-[#8fa3bd]"
                onClick={onClose}
                aria-label="Close step details"
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            </div>

            {/* ── Status + timing strip ── */}
            <div className="flex items-center gap-[18px] px-5 py-[10px] border-b border-[rgba(148,163,184,0.08)] flex-shrink-0">
              {/* Status pill */}
              <span
                className="inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-full text-[9.5px] font-[800] tracking-[0.1em] uppercase border"
                style={{
                  color:       statusColor,
                  background:  `${statusColor}14`,
                  borderColor: `${statusColor}28`,
                }}
              >
                <span
                  className="w-[5px] h-[5px] rounded-full flex-shrink-0"
                  style={{ background: statusColor }}
                />
                {node.status.replace(/_/g, ' ')}
              </span>

              {/* Timing */}
              {step && (
                <div className="flex items-center gap-[12px] text-[10.5px] text-[#4e6480] ml-auto">
                  {step.startedAt && (
                    <span title={step.startedAt}>
                      <span className="text-[#3a5068] font-[800] tracking-[0.08em] uppercase text-[9px] mr-[4px]">started</span>
                      {fmtTime(step.startedAt)}
                    </span>
                  )}
                  {duration && (
                    <span className="font-mono text-[#34d399] font-bold">{duration}</span>
                  )}
                </div>
              )}
            </div>

            {/* ── Body: loading / error / payloads ── */}
            <div className="flex-1 overflow-y-auto px-5 py-4 sds-body-scroll flex flex-col gap-[18px]">
              {fetchState.kind === 'loading' && (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-[#4e6480]">
                  <div className="rsb-spinner" />
                  <p className="text-[13px] font-bold text-[#6b849e] m-0">Loading step…</p>
                </div>
              )}

              {fetchState.kind === 'error' && (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-[#4e6480]">
                  <div className="text-[28px] opacity-40 leading-none">⚠</div>
                  <p className="text-[13px] font-bold text-[#6b849e] m-0 text-center">
                    {fetchState.message}
                  </p>
                </div>
              )}

              {fetchState.kind === 'ready' && (
                <>
                  <PayloadBlock label="Input payload"  payload={step?.inputPayload}  />
                  <PayloadBlock label="Output payload" payload={step?.outputPayload} />

                  {/* Error detail (failed steps) */}
                  {node.errorCode || node.errorMessage ? (
                    (() => {
                      const friendly = node.status === 'failed'
                        ? classifyStepError(node.errorCode, node.errorMessage)
                        : null
                      const rawError = (
                        <div className="px-[14px] py-3 bg-[rgba(239,68,68,0.07)] border border-[rgba(239,68,68,0.2)] rounded-[10px] text-[11.5px] text-[#fca5a5] leading-[1.55] font-mono">
                          {node.errorCode && (
                            <div className="font-bold mb-[2px]">[{node.errorCode}]</div>
                          )}
                          {node.errorMessage}
                        </div>
                      )
                      return (
                        <div className="flex flex-col gap-[8px]">
                          <span className="text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#ef4444]">
                            Error
                          </span>
                          {friendly ? (
                            <>
                              {/* Plain-English summary */}
                              <div className="px-[14px] py-3 bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.28)] rounded-[10px] text-[12px] text-[#fbe2b3] leading-[1.6]">
                                {friendly}
                              </div>
                              {/* Raw error, collapsed by default */}
                              <details className="group">
                                <summary className="cursor-pointer select-none text-[10.5px] font-semibold text-[#4e6480] hover:text-[#8fa3bd] transition-colors">
                                  Technical details
                                </summary>
                                <div className="mt-2">{rawError}</div>
                              </details>
                            </>
                          ) : (
                            rawError
                          )}
                        </div>
                      )
                    })()
                  ) : null}

                  {canLeaveFeedback(step?.status) ? (
                    <div className="flex flex-col gap-[10px]">
                      <span className="text-[9.5px] font-[800] tracking-[0.14em] uppercase text-[#4e6480]">
                        Feedback
                      </span>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center gap-2 rounded-[10px] border px-3 py-2 text-[12px] font-[800] transition-colors duration-[140ms]"
                          style={{
                            color: selectedFeedback === 1 ? '#22c55e' : '#8fa3bd',
                            background: selectedFeedback === 1 ? 'rgba(34,197,94,0.12)' : 'rgba(6,10,16,0.55)',
                            borderColor: selectedFeedback === 1 ? 'rgba(34,197,94,0.35)' : 'rgba(148,163,184,0.12)',
                          }}
                          disabled={feedbackSaving}
                          onClick={() => handleFeedbackChoice(1)}
                          aria-pressed={selectedFeedback === 1}
                        >
                          <span aria-hidden="true">👍</span>
                          Helpful
                        </button>

                        <button
                          type="button"
                          className="inline-flex items-center justify-center gap-2 rounded-[10px] border px-3 py-2 text-[12px] font-[800] transition-colors duration-[140ms]"
                          style={{
                            color: selectedFeedback === -1 ? '#ef4444' : '#8fa3bd',
                            background: selectedFeedback === -1 ? 'rgba(239,68,68,0.12)' : 'rgba(6,10,16,0.55)',
                            borderColor: selectedFeedback === -1 ? 'rgba(239,68,68,0.35)' : 'rgba(148,163,184,0.12)',
                          }}
                          disabled={feedbackSaving}
                          onClick={() => handleFeedbackChoice(-1)}
                          aria-pressed={selectedFeedback === -1}
                        >
                          <span aria-hidden="true">👎</span>
                          Needs work
                        </button>
                      </div>

                      {selectedFeedback !== null ? (
                        <form className="flex flex-col gap-[8px]" onSubmit={handleFeedbackSubmit}>
                          <label className="text-[10.5px] font-[700] uppercase tracking-[0.08em] text-[#4e6480]" htmlFor="step-feedback-note">
                            Note
                          </label>
                          <textarea
                            id="step-feedback-note"
                            className="min-h-[88px] rounded-[10px] border border-[rgba(148,163,184,0.14)] bg-[rgba(6,10,16,0.6)] px-3 py-2 text-[12px] leading-[1.5] text-[#dce9f7] outline-none placeholder:text-[#4e6480]"
                            placeholder="Optional context for future training data"
                            value={feedbackNote}
                            disabled={feedbackSaving}
                            onChange={(event) => setFeedbackNote(event.target.value)}
                          />
                          <div className="flex items-center justify-between gap-3">
                            {feedbackError ? (
                              <span className="text-[11px] text-[#fca5a5]">{feedbackError}</span>
                            ) : (
                              <span className="text-[11px] text-[#4e6480]">
                                {feedbackSaving ? 'Saving feedback…' : 'Feedback is attached to this run step.'}
                              </span>
                            )}
                            <button
                              type="submit"
                              className="inline-flex items-center justify-center rounded-[10px] border border-[rgba(96,165,250,0.28)] bg-[rgba(59,130,246,0.14)] px-3 py-2 text-[11px] font-[800] uppercase tracking-[0.08em] text-[#93c5fd] disabled:cursor-not-allowed disabled:opacity-60"
                              disabled={feedbackSaving}
                            >
                              Save note
                            </button>
                          </div>
                        </form>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  )
}
